import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Inscription, Text, useResponsive } from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import routes from '@/common/routes';
import { LMNClaim } from '@/modules/MyDocuments/myDocuments.types';

interface Props {
  claims?: LMNClaim[];
  onShowClaims?: () => void;
  claim?: LMNClaim;
}

export const LettersOfMedicalNecessityClaims: React.FC<Props> = ({ claims, onShowClaims, claim }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const normalizedClaims = useMemo(() => {
    if (claim) return [claim];
    return claims || [];
  }, [claims, claim]);

  const getClaimLink = useCallback((claimId: string) => {
    return routes.EXPENSES_TRANSACTION_DETAILS_CLAIM(claimId);
  }, []);

  if (!normalizedClaims.length) {
    if (isMobile) {
      return (
        <Inscription color="textBody" textAlign="center" weight={400} size="medium">
          {`${t('Claim')}: ${EMPTY_FIELD_VALUE}`}
        </Inscription>
      );
    }
    return null;
  }

  if (normalizedClaims.length === 1) {
    const currentClaim = normalizedClaims[0];

    if (!currentClaim?.claimId) {
      return null;
    }

    const formattedLink = getClaimLink(currentClaim.claimId);

    return (
      <Box direction="row" align="center" flex>
        {isMobile && (
          <Text weight={400} size="medium" margin={{ right: 'spacing4' }}>
            {`${t('Claim')}: `}
          </Text>
        )}
        <a
          href={formattedLink}
          data-testid="lmn-single-claim_link"
          target="_blank"
          style={{ textDecoration: 'none', minWidth: 0 }}
          rel="noreferrer"
        >
          <Box direction="row" align="center" style={{ cursor: 'pointer' }}>
            <Inscription
              color="textAccent"
              textAlign="center"
              weight={400}
              size={claim ? '16px' : '14px'}
              truncate
            >
              {currentClaim.providerName || 'Provider'}
            </Inscription>
          </Box>
        </a>
      </Box>
    );
  }

  return (
    <Box
      direction="row"
      align="center"
      style={{ cursor: 'pointer' }}
      onClick={onShowClaims}
      data-testid="lmn-multiple-claims_button"
    >
      {isMobile && (
        <Text weight={400} size="medium" margin={{ right: 'spacing4' }}>
          {`${t('Claim')}: `}
        </Text>
      )}
      <Inscription color="textAccent" textAlign="center">
        Show Claims ({normalizedClaims.length})
      </Inscription>
    </Box>
  );
};
